import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface Permission {
  _id: string;
  name: string;
  category: string;
}

export interface Subscriber {
  _id: string;
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  companyLogo?: string;
}

export interface BaseRole {
  _id: string;
  identification: string;
  name: string;
  level: string;
  subscriberId?: string;
  baseRole: string[];
  userCount?: number;
  locked?: boolean;
}

export interface Role extends BaseRole {
  permissions: string[]; // Array of Permission ObjectIds
}

export interface RoleWithPermissions extends BaseRole {
  permissions: Permission[]; // Populated permissions
}

interface ApiResponse<T> {
  message: string;
  roles?: T[];
  role?: T;
  subscribers?: Subscriber[];
}

// Pagination interfaces for subscribers
export interface PagedSubscriberRequest {
  pageIndex: number; // 1-based indexing (page 1, 2, 3...)
  pageSize: number;
  sortField?: string;
  sortOrder?: 1 | -1;
  filters?: {
    searchText?: string;
    status?: string;
    userStatus?: boolean;
    industries?: string[];
    operationalRegions?: string[];
  };
}

export interface PagedSubscriberResponse {
  message: string;
  data: Subscriber[];
  metadata: Array<{
    total: number;
    page: number;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class RoleService {
  constructor(private http: HttpClient) {}

  getGlobalRoles(): Observable<RoleWithPermissions[]> {
    return this.http.get<ApiResponse<RoleWithPermissions>>(`${environment.apiUrl}/roles/global?populate=permissions`)
      .pipe(map(response => response.roles || []));
  }

  getBaseRoles(): Observable<RoleWithPermissions[]> {
    return this.http.get<ApiResponse<RoleWithPermissions>>(`${environment.apiUrl}/roles/base`)
      .pipe(map(response => response.roles || []));
  }

  getSubscriberRoles(subscriberId: string): Observable<RoleWithPermissions[]> {
    return this.http.get<ApiResponse<RoleWithPermissions>>(`${environment.apiUrl}/roles/subscriber/${subscriberId}?populate=permissions`)
      .pipe(map(response => response.roles || []));
  }

  createCustomRole(role: Partial<Role>): Observable<RoleWithPermissions> {
    return this.http.post<ApiResponse<RoleWithPermissions>>(`${environment.apiUrl}/roles`, role)
      .pipe(map(response => response.role!));
  }

  getAvailableRoles(): Observable<RoleWithPermissions[]> {
    return this.http.get<ApiResponse<RoleWithPermissions>>(`${environment.apiUrl}/roles/available?populate=permissions`)
      .pipe(map(response => response.roles || []));
  }

  getAllSubscribers(): Observable<Subscriber[]> {
    return this.http.get<ApiResponse<Subscriber>>(`${environment.apiUrl}/subscribers`)
      .pipe(map(response => response.subscribers || []));
  }

  // Get paged subscribers with advanced filtering
  getPagedSubscribers(params: PagedSubscriberRequest): Observable<PagedSubscriberResponse> {
    return this.http.post<{ message: string, data: Subscriber[], metadata: Array<{ total: number, page: number }> }>(`${environment.apiUrl}/subscribers/paged`, params).pipe(
      map(response => ({
        message: response.message,
        data: response.data || [],
        metadata: response.metadata || []
      }))
    );
  }

  // Update a role (including base roles)
  updateRole(roleId: string, updates: Partial<Role>): Observable<RoleWithPermissions> {
    return this.http.put<ApiResponse<RoleWithPermissions>>(`${environment.apiUrl}/roles/${roleId}`, updates)
      .pipe(map(response => response.role!));
  }

  // Get all roles with filter options
  getRoles(filters: any = {}): Observable<RoleWithPermissions[]> {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null) {
        params.append(key, filters[key].toString());
      }
    });
    
    return this.http.get<ApiResponse<RoleWithPermissions>>(`${environment.apiUrl}/roles?${params.toString()}`)
      .pipe(map(response => response.roles || []));
  }

  // Delete a custom role
  deleteRole(roleId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${environment.apiUrl}/roles/${roleId}`);
  }
}